import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { X, Save, AlertCircle, Search } from "lucide-react";
import { toast } from "react-toastify";
import amsProjectAuditService, { AmsProjectAudit } from "../../../services/amsProjectAuditService";
import { AmsAuditApproval } from "../../../services/amsAuditApprovalService";
import { userService } from "../../../services/userService";
import { User } from "../../../types/user";
import Label from "../../../components/form/Label";
import Input from "../../../components/form/input/InputField";

/** A rejected section/checkpoint the requester needs to address, with the approver's reason. */
interface RejectedItem {
  label: string;
  comment?: string;
}

/**
 * Collect the approver's rejection feedback (sections + checkpoints) so the requester
 * sees *why* the audit was rejected right where they edit it — mirrors the audit card.
 */
function deriveRejectionFeedback(
  approval?: AmsAuditApproval | null,
): { sections: RejectedItem[]; checkpoints: RejectedItem[] } | null {
  if (!approval || approval.overallStatus !== 'rejected') return null;

  const sections: RejectedItem[] = [];
  const s = approval.sections;
  if (s?.auditType?.status === 'rejected') sections.push({ label: 'Audit info', comment: s.auditType.approverComments });
  if (s?.auditors?.status === 'rejected') sections.push({ label: 'Auditors', comment: s.auditors.approverComments });
  if (s?.assetsAndEquipment?.status === 'rejected') sections.push({ label: 'Assets & equipment', comment: s.assetsAndEquipment.approverComments });

  const checkpoints: RejectedItem[] = [];
  [
    ...(approval.checkpointApprovals?.projectCheckpoints ?? []),
    ...(approval.checkpointApprovals?.assetCheckpoints ?? []),
  ].forEach((cp) => {
    if (cp.status === 'rejected') checkpoints.push({ label: cp.checkpointId?.name || 'Checkpoint', comment: cp.approverComments });
  });

  if (sections.length === 0 && checkpoints.length === 0) return null;
  return { sections, checkpoints };
}

/** Convert DD/MM/YYYY or YYYY-MM-DD to YYYY-MM-DD for input[type=date] */
function toInputDate(dateStr: string | undefined): string {
  if (!dateStr || !dateStr.trim()) return '';
  const s = dateStr.trim();
  const ddmmyyyy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    return `${y}-${(m ?? '').padStart(2, '0')}-${(d ?? '').padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return '';
}

/** Parse to Date for comparison */
function parseToDate(dateStr: string | undefined): Date | null {
  if (!dateStr || !dateStr.trim()) return null;
  const s = dateStr.trim();
  const ddmmyyyy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    const date = new Date(parseInt(y ?? '', 10), parseInt(m ?? '', 10) - 1, parseInt(d ?? '', 10));
    return isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(s.slice(0, 10));
  return isNaN(date.getTime()) ? null : date;
}

interface AmsProjectAuditEditModalProps {
  open: boolean;
  onClose: () => void;
  audit: AmsProjectAudit;
  projectStartDate?: string;
  projectEndDate?: string;
  /** Approval record for this audit — drives the rejection-feedback banner. */
  approval?: AmsAuditApproval | null;
  /** Project's assigned auditor IDs — limits the selectable auditors for this audit. */
  projectAuditors?: string[];
  /** When true, render only form content (for use inside RightDrawer) */
  embedded?: boolean;
  onSuccess?: () => void;
}

const buildEditAuditFormSchema = (projectStartDate?: string, projectEndDate?: string) =>
  yup.object({
    scope: yup
      .number()
      .required('Scope is required')
      .min(0, 'Scope must be at least 0')
      .max(100, 'Scope must not be greater than 100 percent')
      .typeError('Scope must be a number'),
    auditors: yup
      .array()
      .of(yup.string().required())
      .min(1, 'At least one auditor is required')
      .required('At least one auditor is required'),
    auditStartDate: yup
      .string()
      .optional()
      .test('in-range', 'Audit start date must be between project start and end date', function (value) {
        if (!value || !value.trim()) return true;
        const start = parseToDate(projectStartDate);
        const end = parseToDate(projectEndDate);
        const audit = parseToDate(value);
        if (!audit) return false;
        if (start && audit < start) return false;
        if (end && audit > end) return false;
        return true;
      }),
  });

/** Map an audit's populated auditors (or raw IDs) to a plain string[] of user IDs. */
const auditorIdsOf = (audit: AmsProjectAudit): string[] =>
  (audit.auditors ?? []).map((a: any) => String(typeof a === 'object' ? (a._id || a.id) : a));

type EditAuditFormValues = yup.InferType<ReturnType<typeof buildEditAuditFormSchema>>;

export default function AmsProjectAuditEditModal({
  open,
  onClose,
  audit,
  projectStartDate,
  projectEndDate,
  approval,
  projectAuditors = [],
  embedded = false,
  onSuccess,
}: AmsProjectAuditEditModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [auditorSearch, setAuditorSearch] = useState('');
  const rejectionFeedback = deriveRejectionFeedback(approval);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditAuditFormValues>({
    resolver: yupResolver(buildEditAuditFormSchema(projectStartDate, projectEndDate)) as any,
    defaultValues: {
      scope: audit.scope ?? undefined,
      auditors: auditorIdsOf(audit),
      auditStartDate: audit.auditStartDate ? toInputDate(audit.auditStartDate) : undefined,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        scope: audit.scope ?? undefined,
        auditors: auditorIdsOf(audit),
        auditStartDate: audit.auditStartDate ? toInputDate(audit.auditStartDate) : undefined,
      });
      setAuditorSearch('');
    }
  }, [open, audit, reset]);

  // Load active users once the drawer opens (auditors are picked from the project's own auditor pool).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingUsers(true);
      try {
        const response = await userService.getUsers({ status: 'active', limit: 1000, populate: 'role' });
        if (!cancelled) setUsers(response.users);
      } catch (error: any) {
        console.error('Failed to fetch users:', error);
        if (!cancelled) toast.error('Failed to load auditors');
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Only the project's assigned auditors are eligible; fall back to all users if none set.
  const eligibleAuditors = projectAuditors.length > 0
    ? users.filter((u) => projectAuditors.some((id) => String(id) === String(u._id || u.id)))
    : users;
  const filteredAuditors = auditorSearch
    ? eligibleAuditors.filter(
        (u) =>
          u.name.toLowerCase().includes(auditorSearch.toLowerCase()) ||
          u.email.toLowerCase().includes(auditorSearch.toLowerCase()),
      )
    : eligibleAuditors;

  const onSubmit = async (data: EditAuditFormValues) => {
    setSubmitting(true);
    try {
      await amsProjectAuditService.updateAudit(audit._id, {
        scope: data.scope,
        auditors: (data.auditors ?? []) as string[],
        auditStartDate: data.auditStartDate || undefined,
      });

      toast.success('Audit updated successfully');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      console.error('Failed to update audit:', error);
      toast.error(error?.response?.data?.message || 'Failed to update audit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const formContent = (
    <div className={embedded ? 'p-0' : 'p-6 overflow-y-auto flex-1'}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Rejection feedback — why the approver sent this audit back (read-only) */}
        {rejectionFeedback && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle size={16} className="shrink-0" />
              <h4 className="text-sm font-semibold">This audit was rejected</h4>
            </div>
            <p className="mt-1 text-xs text-red-700">
              The approver sent it back for the following. Address these, then resubmit for review.
            </p>

            {rejectionFeedback.sections.length > 0 && (
              <ul className="mt-3 space-y-2">
                {rejectionFeedback.sections.map((item) => (
                  <li key={item.label} className="text-xs">
                    <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 font-medium text-red-800">
                      {item.label}
                    </span>
                    {item.comment ? (
                      <span className="ml-2 text-red-700 italic">"{item.comment}"</span>
                    ) : (
                      <span className="ml-2 text-red-500">No reason provided</span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {rejectionFeedback.checkpoints.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-red-700">
                  Rejected checkpoints ({rejectionFeedback.checkpoints.length})
                </p>
                <ul className="mt-1.5 space-y-1">
                  {rejectionFeedback.checkpoints.map((item, i) => (
                    <li key={`${item.label}-${i}`} className="text-xs text-red-700">
                      <span className="font-medium">{item.label}</span>
                      {item.comment && <span className="ml-1 italic">— "{item.comment}"</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="mt-3 text-[11px] text-red-600">
              Scope, auditors and start date are editable below. Assets &amp; checkpoints are updated from their own sections.
            </p>
          </div>
        )}

        {/* Audit Type - Read Only */}
        <div>
          <Label htmlFor="auditType">Audit Type</Label>
          <div className="px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700">
            {typeof audit.auditTypeId === 'string' ? audit.auditTypeId : audit.auditTypeId?.name}
          </div>
          <p className="mt-1 text-xs text-gray-500">Audit type cannot be changed</p>
        </div>

        {/* Frequency - Read Only */}
        <div>
          <Label>Frequency</Label>
          <div className="px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700">
            {audit.auditFrequency === 'yearly' && 'Yearly'}
            {audit.auditFrequency === 'half_yearly' && 'Half Yearly'}
            {audit.auditFrequency === 'quarterly' && 'Quarterly'}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Scope (Resource) - Editable */}
          <div>
            <Label htmlFor="scope">Scope (Resource) *</Label>
            <Controller
              name="scope"
              control={control}
              render={({ field: { onChange, value, onBlur } }) => (
                <Input
                  id="scope"
                  type="number"
                  placeholder="Enter scope (e.g. 100)"
                  value={value ?? ''}
                  onChange={(e) => {
                    const numValue = e.target.value === '' ? undefined : Number(e.target.value);
                    onChange(numValue);
                  }}
                  onBlur={onBlur}
                  min={0}
                  max={100}
                  error={!!errors.scope}
                  hint={errors.scope?.message}
                />
              )}
            />
          </div>

          {/* Audit Start Date - Editable */}
          <div>
            <Label htmlFor="auditStartDate">Audit Start Date</Label>
            <Controller
              name="auditStartDate"
              control={control}
              render={({ field: { onChange, value, onBlur } }) => (
                <input
                  id="auditStartDate"
                  type="date"
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value || undefined)}
                  onBlur={onBlur}
                  min={projectStartDate ? toInputDate(projectStartDate) : undefined}
                  max={projectEndDate ? toInputDate(projectEndDate) : undefined}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              )}
            />
            {errors.auditStartDate && (
              <p className="mt-1 text-sm text-red-600">{errors.auditStartDate.message}</p>
            )}
            {(projectStartDate || projectEndDate) && (
              <p className="mt-1 text-xs text-gray-500">Must be between project start and end date.</p>
            )}
          </div>
        </div>

        {/* Auditors - Editable (limited to the project's assigned auditors) */}
        <Controller
          name="auditors"
          control={control}
          render={({ field: { onChange, value } }) => {
            const selected = (value ?? []) as string[];
            const auditorsRejected = approval?.sections?.auditors?.status === 'rejected';
            const toggle = (id: string, checked: boolean) =>
              onChange(checked ? [...selected, id] : selected.filter((v) => v !== id));
            return (
              <div>
                <Label htmlFor="auditors">
                  Auditors *
                  {auditorsRejected && (
                    <span className="ml-2 inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                      Rejected — please update
                    </span>
                  )}
                </Label>
                <div className={`rounded-lg border p-3 ${auditorsRejected ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="relative mb-3">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search auditors..."
                      value={auditorSearch}
                      onChange={(e) => setAuditorSearch(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-9 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    {auditorSearch && (
                      <button
                        type="button"
                        onClick={() => setAuditorSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                    {loadingUsers ? (
                      <div className="py-6 text-center text-sm text-gray-500">Loading...</div>
                    ) : filteredAuditors.length === 0 ? (
                      <div className="py-6 text-center text-sm text-gray-500">
                        {auditorSearch ? `No matches for "${auditorSearch}"` : 'No auditors available'}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {filteredAuditors.map((auditor) => {
                          const auditorId = String(auditor._id || auditor.id);
                          const isChecked = selected.includes(auditorId);
                          return (
                            <label
                              key={auditorId}
                              className="flex cursor-pointer items-center gap-3 p-3 transition-colors hover:bg-emerald-50"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => toggle(auditorId, e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-gray-900">{auditor.name}</p>
                                <p className="truncate text-xs text-gray-500">{auditor.email}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {selected.length > 0 && (
                    <p className="mt-3 text-sm text-green-600">
                      {selected.length} auditor{selected.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                  {errors.auditors && (
                    <p className="mt-2 text-sm text-red-600">{(errors.auditors as any)?.message}</p>
                  )}
                </div>
              </div>
            );
          }}
        />

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save size={16} />
                Update Audit
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Edit Audit</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        {formContent}
      </div>
    </div>
  );
}
